import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { fetchTenantUsageMetrics } from '../services/tenantMetrics';
import { transformKeys } from '../services/transformers';
import { logTenantMetricsDebug } from '../utils/tenantMetricsDebug';
import type {
  AuthBootstrapSubscriptionSummary,
  TenantUsageSummaryEvent,
} from '../types';

interface TenantUsageMetricsContextValue {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  aiSubscription: AuthBootstrapSubscriptionSummary | null;
  reverseIvrSubscription: AuthBootstrapSubscriptionSummary | null;
  lastPayload: TenantUsageSummaryEvent | null;
  canViewUsageMetrics: boolean;
  refresh: (reason?: string) => Promise<void>;
}

const TenantUsageMetricsContext = createContext<TenantUsageMetricsContextValue | undefined>(undefined);

/**
 * Provide one shared tenant usage metrics store for shell consumers.
 */
export function TenantUsageMetricsProvider({ children }: { children: ReactNode }) {
  const { currentTenant, user, capabilities, subscriptions } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();
  const tenantId = currentTenant?.id || user?.tenantId || null;
  const canViewUsageMetrics = Boolean(capabilities?.canViewUsageMetrics);
  const initialAiSubscription = subscriptions?.ai?.summary ?? null;
  const initialReverseIvrSubscription = subscriptions?.reverseIvr?.summary ?? null;
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [aiSubscription, setAiSubscription] = useState<AuthBootstrapSubscriptionSummary | null>(initialAiSubscription);
  const [reverseIvrSubscription, setReverseIvrSubscription] = useState<AuthBootstrapSubscriptionSummary | null>(initialReverseIvrSubscription);
  const [lastPayload, setLastPayload] = useState<TenantUsageSummaryEvent | null>(null);
  const logDebug = useCallback((message: string, data?: unknown) => {
    logTenantMetricsDebug('TenantUsageMetricsProvider', message, data);
  }, []);

  useEffect(() => {
    logDebug('tenant changed, resetting cached usage metrics state', {
      tenantId,
      hasInitialAiSubscription: Boolean(initialAiSubscription),
      hasInitialReverseIvrSubscription: Boolean(initialReverseIvrSubscription),
    });
    setAiSubscription(initialAiSubscription);
    setReverseIvrSubscription(initialReverseIvrSubscription);
    setLastPayload(null);
  }, [initialAiSubscription, initialReverseIvrSubscription, logDebug, tenantId]);

  const refresh = useCallback(async (reason = 'manual_recovery') => {
    if (!tenantId || !canViewUsageMetrics) {
      logDebug('refresh skipped because tenant or permission is missing', {
        reason,
        tenantId,
        canViewUsageMetrics,
      });
      return;
    }

    logDebug('refresh started', {
      reason,
      tenantId,
      isConnected: state.isConnected,
      hasLastPayload: Boolean(lastPayload),
    });
    setIsRefreshing(true);
    try {
      const response = await fetchTenantUsageMetrics();
      setAiSubscription(response.aiSubscription ?? null);
      setReverseIvrSubscription(response.reverseIvrSubscription ?? null);
      setLastPayload(response);
      logDebug('refresh completed', {
        reason,
        tenantId,
        hasAiSubscription: Boolean(response.aiSubscription),
        hasReverseIvrSubscription: Boolean(response.reverseIvrSubscription),
      });
    } catch (error) {
      logDebug('refresh failed', {
        reason,
        tenantId,
        error,
      });
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [canViewUsageMetrics, lastPayload, logDebug, state.isConnected, tenantId]);

  const handleEvent = useCallback(
    (data: TenantUsageSummaryEvent, action: string) => {
      if (action !== 'create' && action !== 'update' && action !== 'delete') {
        logDebug('event ignored because action is not handled', { tenantId, action, data });
        return;
      }

      const normalized = transformKeys<TenantUsageSummaryEvent>(data);
      if (tenantId && normalized.id && normalized.id !== tenantId) {
        logDebug('event ignored because payload tenant does not match current tenant', {
          tenantId,
          payloadTenantId: normalized.id,
          action,
        });
        return;
      }

      logDebug('event received', {
        tenantId,
        action,
        hasAiSubscription: Boolean(normalized.aiSubscription),
        hasReverseIvrSubscription: Boolean(normalized.reverseIvrSubscription),
        payload: normalized,
      });
      setAiSubscription(normalized.aiSubscription ?? null);
      setReverseIvrSubscription(normalized.reverseIvrSubscription ?? null);
      setLastPayload(normalized);
    },
    [logDebug, tenantId],
  );

  useEffect(() => {
    if (!tenantId || !canViewUsageMetrics || !state.isConnected) {
      logDebug('subscription skipped because preconditions are not satisfied', {
        tenantId,
        canViewUsageMetrics,
        isConnected: state.isConnected,
      });
      setIsSubscribed(false);
      return;
    }

    logDebug('creating usage metrics observer subscription', {
      tenantId,
      canViewUsageMetrics,
      isConnected: state.isConnected,
    });
    const observerId = addObserver(
      'tenant_usage_metrics',
      { data: { target: tenantId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
        includeUpdateEvents: true,
        includeDeleteEvents: true,
      },
    );
    setIsSubscribed(true);
    logDebug('usage metrics observer subscribed', { tenantId, observerId });

    return () => {
      logDebug('cleaning up usage metrics observer subscription', { tenantId, observerId });
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [addObserver, canViewUsageMetrics, handleEvent, logDebug, removeObserver, state.isConnected, tenantId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const refreshIfRecoveryIsNeeded = () => {
      if (!tenantId || !canViewUsageMetrics) {
        logDebug('recovery skipped because tenant or permission is missing', {
          tenantId,
          canViewUsageMetrics,
        });
        return;
      }
      if (state.isConnected && lastPayload) {
        logDebug('recovery skipped because provider believes connection is healthy', {
          tenantId,
          isConnected: state.isConnected,
          hasLastPayload: Boolean(lastPayload),
        });
        return;
      }
      logDebug('recovery refresh requested', {
        tenantId,
        isConnected: state.isConnected,
        hasLastPayload: Boolean(lastPayload),
      });
      void refresh('usage_metrics_recovery');
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        logDebug('visibilitychange ignored because document is not visible', {
          tenantId,
          documentVisibilityState: document.visibilityState,
        });
        return;
      }
      logDebug('visibilitychange detected visible document for recovery check', { tenantId });
      refreshIfRecoveryIsNeeded();
    };

    window.addEventListener('focus', refreshIfRecoveryIsNeeded);
    window.addEventListener('pageshow', refreshIfRecoveryIsNeeded);
    window.addEventListener('online', refreshIfRecoveryIsNeeded);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshIfRecoveryIsNeeded);
      window.removeEventListener('pageshow', refreshIfRecoveryIsNeeded);
      window.removeEventListener('online', refreshIfRecoveryIsNeeded);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canViewUsageMetrics, lastPayload, logDebug, refresh, state.isConnected, tenantId]);

  const value = useMemo<TenantUsageMetricsContextValue>(
    () => ({
      isConnected: state.isConnected,
      isSubscribed,
      isRefreshing,
      aiSubscription,
      reverseIvrSubscription,
      lastPayload,
      canViewUsageMetrics,
      refresh,
    }),
    [
      aiSubscription,
      canViewUsageMetrics,
      isRefreshing,
      isSubscribed,
      lastPayload,
      refresh,
      reverseIvrSubscription,
      state.isConnected,
    ],
  );

  return (
    <TenantUsageMetricsContext.Provider value={value}>
      {children}
    </TenantUsageMetricsContext.Provider>
  );
}

export function useTenantUsageMetricsContext(): TenantUsageMetricsContextValue {
  const context = useContext(TenantUsageMetricsContext);
  if (!context) {
    throw new Error('useTenantUsageMetricsContext must be used within TenantUsageMetricsProvider');
  }
  return context;
}
